import type { ReactElement, ReactNode } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type Tone = 'success' | 'attention' | 'failure' | 'working';

export interface Notice {
  id: string;
  tone: Tone;
  text: string;
  /** Anything a person can act on carries the action inline. */
  action?: { label: string; onSelect: () => void };
  /** Success disappears on its own; attention and failure wait to be dismissed. */
  ttlMs?: number;
}

interface Bar {
  notices: Notice[];
  show: (notice: Omit<Notice, 'id'> & { id?: string }) => string;
  dismiss: (id: string) => void;
}

const NoticesContext = createContext<Bar | null>(null);

/** The one notice slot under the header, in every screen, so a person learns one place to look. */
export function NoticesProvider({ children }: { children: ReactNode }): ReactElement {
  const [notices, setNotices] = useState<Notice[]>([]);
  const timers = useRef(new Map<string, number>());
  const dismiss = useCallback((id: string) => {
    setNotices((all) => all.filter((n) => n.id !== id));
    const t = timers.current.get(id);
    if (t) window.clearTimeout(t);
    timers.current.delete(id);
  }, []);
  const show = useCallback(
    (notice: Omit<Notice, 'id'> & { id?: string }) => {
      const id =
        notice.id ?? `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
      const ttlMs = notice.ttlMs ?? (notice.tone === 'success' ? 6000 : undefined);
      setNotices((all) => [...all.filter((n) => n.id !== id), { ...notice, id, ttlMs }]);
      if (ttlMs)
        timers.current.set(
          id,
          window.setTimeout(() => dismiss(id), ttlMs),
        );
      return id;
    },
    [dismiss],
  );
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);
  const value = useMemo(() => ({ notices, show, dismiss }), [notices, show, dismiss]);
  return <NoticesContext.Provider value={value}>{children}</NoticesContext.Provider>;
}

export function useNotices(): Bar {
  const bar = useContext(NoticesContext);
  if (!bar) throw new Error('useNotices outside NoticesProvider');
  return bar;
}

const GLYPH_ICON: Record<Tone, IconName | null> = {
  success: 'check',
  attention: 'alert',
  failure: 'alert',
  working: null,
};

export function NoticeBar({ children }: { children?: ReactNode }): ReactElement {
  const { notices, dismiss } = useNotices();
  return (
    <div className="notice-slot" aria-live="polite">
      {children}
      {notices.map((n) => (
        <div
          key={n.id}
          className={`notice notice-${n.tone}`}
          role={n.tone === 'failure' ? 'alert' : 'status'}
        >
          {GLYPH_ICON[n.tone] ? (
            <span className="notice-glyph" aria-hidden="true">
              <Icon name={GLYPH_ICON[n.tone]!} size={14} />
            </span>
          ) : null}
          <span className="grow">{n.text}</span>
          {n.action ? (
            <button type="button" className="btn btn-sm" onClick={n.action.onSelect}>
              {n.action.label}
            </button>
          ) : null}
          {n.tone === 'success' && n.ttlMs ? <Countdown ms={n.ttlMs} /> : null}
          {n.tone !== 'success' ? (
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              aria-label="Dismiss"
              onClick={() => dismiss(n.id)}
            >
              <Icon name="close" size={13} />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function Countdown({ ms }: { ms: number }): ReactElement {
  const [left, setLeft] = useState(Math.ceil(ms / 1000));
  useEffect(() => {
    const t = window.setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, []);
  return <span className="notice-timer">{left}s</span>;
}
